import AddIcon from "@mui/icons-material/Add"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import * as hrLeaveService from "../../services/hrLeaveService"


// =========================================================
// CONSTANTES
// =========================================================

const TODAY =
  new Date()
    .toISOString()
    .slice(0, 10)


const EMPTY_FORM = {
  employee_id: "",
  leave_type_id: "",
  start_date: TODAY,
  end_date: TODAY,
  reason: "",
  notes: "",
}


const EMPTY_DECISION = {
  reason: "",
}


// =========================================================
// HELPERS
// =========================================================

const formatDateFr = (value) => {
  if (!value) {
    return "-"
  }

  const match =
    String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    )

  if (!match) {
    return value
  }

  return `${match[3]}/${match[2]}/${match[1]}`
}


const calculateDays = (
  startDate,
  endDate
) => {
  if (
    !startDate ||
    !endDate
  ) {
    return 0
  }

  const start =
    new Date(
      `${startDate}T00:00:00`
    )

  const end =
    new Date(
      `${endDate}T00:00:00`
    )

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    ) ||
    end < start
  ) {
    return 0
  }

  return (
    Math.floor(
      (
        end.getTime() -
        start.getTime()
      ) / 86400000
    ) + 1
  )
}


const getErrorMessage = (
  error,
  fallback
) => {
  const detail =
    error
      ?.response
      ?.data
      ?.detail

  if (
    typeof detail ===
    "string"
  ) {
    return detail
  }

  if (
    detail &&
    typeof detail ===
      "object" &&
    !Array.isArray(
      detail
    )
  ) {
    if (
      detail.database_error
    ) {
      console.error(
        "Erreur backend / PostgreSQL :",
        detail.database_error
      )
    }

    return (
      detail.message ||
      fallback
    )
  }

  return (
    error
      ?.response
      ?.data
      ?.message ||
    error?.message ||
    fallback
  )
}


const getLeaveTypeLabel = (
  leaveType
) => {
  if (!leaveType) {
    return "-"
  }

  const code =
    leaveType.code || ""

  const name =
    leaveType.name ||
    leaveType.label ||
    ""

  if (
    code &&
    name
  ) {
    return `${code} - ${name}`
  }

  return (
    name ||
    code ||
    `Type #${leaveType.id}`
  )
}


const getEmployeeLabel = (
  employee
) => {
  if (!employee) {
    return "-"
  }

  const code =
    employee.employee_code ||
    ""

  const name =
    employee.employee_name ||
    [
      employee.first_name,
      employee.last_name,
    ]
      .filter(Boolean)
      .join(" ")

  if (
    code &&
    name
  ) {
    return `${code} - ${name}`
  }

  return (
    name ||
    code ||
    `Employé #${employee.id}`
  )
}


const normalizeStatus = (
  value
) =>
  String(
    value || ""
  )
    .trim()
    .toUpperCase()


const isPendingStatus = (
  status
) =>
  status === "PENDING" ||
  status === "REQUESTED"


const getStatusConfig = (
  value
) => {
  const status =
    normalizeStatus(
      value
    )

  const statuses = {
    APPROVED: {
      label: "Approuvé",
      color: "success",
    },

    PENDING: {
      label: "En attente",
      color: "warning",
    },

    REQUESTED: {
      label: "En attente",
      color: "warning",
    },

    REJECTED: {
      label: "Refusé",
      color: "error",
    },

    DECLINED: {
      label: "Refusé",
      color: "error",
    },

    CANCELLED: {
      label: "Annulé",
      color: "default",
    },
  }

  return (
    statuses[status] || {
      label:
        value || "-",

      color:
        "default",
    }
  )
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: 116,
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            fontWeight={900}
            sx={{
              mt: 0.15,
              fontSize: {
                xs: "1.6rem",
                md: "1.9rem",
              },
              lineHeight: 1.15,
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function LeaveRequestsPage() {

  // =======================================================
  // STATE
  // =======================================================

  const [
    rows,
    setRows,
  ] = useState([])

  const [
    employees,
    setEmployees,
  ] = useState([])

  const [
    leaveTypes,
    setLeaveTypes,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  })

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    error,
    setError,
  ] = useState("")

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all")

  const [
    employeeFilter,
    setEmployeeFilter,
  ] = useState("all")

  const [
    leaveTypeFilter,
    setLeaveTypeFilter,
  ] = useState("all")

  const [
    formDialogOpen,
    setFormDialogOpen,
  ] = useState(false)

  const [
    decisionDialogOpen,
    setDecisionDialogOpen,
  ] = useState(false)

  const [
    editingRequest,
    setEditingRequest,
  ] = useState(null)

  const [
    selectedRequest,
    setSelectedRequest,
  ] = useState(null)

  const [
    decisionAction,
    setDecisionAction,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
  })

  const [
    decision,
    setDecision,
  ] = useState({
    ...EMPTY_DECISION,
  })

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // MESSAGE
  // =======================================================

  const showMessage =
    useCallback(
      (
        message,
        severity = "success"
      ) => {
        setSnackbar({
          open: true,
          severity,
          message,
        })
      },
      []
    )


  // =======================================================
  // FORM DATA
  // =======================================================

  const loadFormData =
    useCallback(
      async () => {
        try {
          const result =
            await hrLeaveService
              .getHrLeaveFormData()

          setEmployees(
            result?.employees ||
            []
          )

          setLeaveTypes(
            result?.leave_types ||
            []
          )

        } catch (err) {
          showMessage(
            getErrorMessage(
              err,
              "Impossible de charger les employés et les types de congé."
            ),
            "error"
          )
        }
      },
      [
        showMessage,
      ]
    )


  // =======================================================
  // LIST
  // =======================================================

  const loadData =
    useCallback(
      async () => {
        setLoading(true)
        setError("")

        try {
          const result =
            await hrLeaveService
              .getHrLeaveRequests({
                search:
                  search.trim() ||
                  undefined,

                status:
                  statusFilter,

                employee_id:
                  employeeFilter,

                leave_type_id:
                  leaveTypeFilter,
              })

          const items =
            Array.isArray(
              result
            )
              ? result
              : result?.items ||
                result?.data ||
                []

          setRows(
            items
          )

          if (
            result?.summary
          ) {
            setSummary({
              total:
                Number(
                  result.summary.total ||
                  0
                ),

              pending:
                Number(
                  result.summary.pending ||
                  0
                ),

              approved:
                Number(
                  result.summary.approved ||
                  0
                ),

              rejected:
                Number(
                  result.summary.rejected ||
                  0
                ),

              cancelled:
                Number(
                  result.summary.cancelled ||
                  0
                ),
            })

          } else {
            const calculated = {
              total:
                items.length,

              pending:
                0,

              approved:
                0,

              rejected:
                0,

              cancelled:
                0,
            }

            items.forEach(
              (item) => {
                const status =
                  normalizeStatus(
                    item.status
                  )

                if (
                  isPendingStatus(
                    status
                  )
                ) {
                  calculated.pending += 1
                }

                if (
                  status ===
                  "APPROVED"
                ) {
                  calculated.approved += 1
                }

                if (
                  status ===
                    "REJECTED" ||
                  status ===
                    "DECLINED"
                ) {
                  calculated.rejected += 1
                }

                if (
                  status ===
                  "CANCELLED"
                ) {
                  calculated.cancelled += 1
                }
              }
            )

            setSummary(
              calculated
            )
          }

        } catch (err) {
          setError(
            getErrorMessage(
              err,
              "Impossible de charger les congés et permissions."
            )
          )

        } finally {
          setLoading(false)
        }
      },
      [
        search,
        statusFilter,
        employeeFilter,
        leaveTypeFilter,
      ]
    )


  // =======================================================
  // EFFECTS
  // =======================================================

  useEffect(() => {
    loadFormData()
  }, [
    loadFormData,
  ])


  useEffect(() => {
    const timer =
      setTimeout(
        loadData,
        250
      )

    return () =>
      clearTimeout(
        timer
      )
  }, [
    loadData,
  ])


  // =======================================================
  // DAYS
  // =======================================================

  const requestedDays =
    useMemo(
      () =>
        calculateDays(
          form.start_date,
          form.end_date
        ),
      [
        form.start_date,
        form.end_date,
      ]
    )


  // =======================================================
  // CREATE DIALOG
  // =======================================================

  const openCreateDialog =
    () => {
      setEditingRequest(
        null
      )

      setForm({
        ...EMPTY_FORM,
        start_date: TODAY,
        end_date: TODAY,
      })

      setFormDialogOpen(
        true
      )
    }


  // =======================================================
  // EDIT DIALOG
  // =======================================================

  const openEditDialog =
    (row) => {
      if (
        !isPendingStatus(
          normalizeStatus(
            row.status
          )
        )
      ) {
        showMessage(
          "Seules les demandes en attente peuvent être modifiées.",
          "warning"
        )

        return
      }

      setEditingRequest(
        row
      )

      setForm({
        employee_id:
          row.employee_id ||
          "",

        leave_type_id:
          row.leave_type_id ||
          "",

        start_date:
          row.start_date ||
          TODAY,

        end_date:
          row.end_date ||
          TODAY,

        reason:
          row.reason ||
          "",

        notes:
          row.notes ||
          "",
      })

      setFormDialogOpen(
        true
      )
    }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !form.employee_id
      ) {
        showMessage(
          "Sélectionnez un employé.",
          "warning"
        )

        return
      }

      if (
        !form.leave_type_id
      ) {
        showMessage(
          "Sélectionnez un type de congé ou permission.",
          "warning"
        )

        return
      }

      if (
        !form.start_date ||
        !form.end_date
      ) {
        showMessage(
          "Les dates de début et de fin sont obligatoires.",
          "warning"
        )

        return
      }

      if (
        requestedDays <= 0
      ) {
        showMessage(
          "La période sélectionnée est invalide.",
          "warning"
        )

        return
      }

      const payload = {
        leave_type_id:
          Number(
            form.leave_type_id
          ),

        start_date:
          form.start_date,

        end_date:
          form.end_date,

        requested_days:
          requestedDays,

        reason:
          form.reason.trim() ||
          null,

        notes:
          form.notes.trim() ||
          null,
      }

      setSaving(true)

      try {
        let result

        if (
          editingRequest
        ) {
          result =
            await hrLeaveService
              .updateHrLeaveRequest(
                editingRequest.id,
                payload
              )

          showMessage(
            result?.message ||
            "Demande modifiée avec succès."
          )

        } else {
          result =
            await hrLeaveService
              .createHrLeaveRequest({
                ...payload,

                employee_id:
                  Number(
                    form.employee_id
                  ),
              })

          showMessage(
            result?.message ||
            "Demande créée avec succès."
          )
        }

        setFormDialogOpen(
          false
        )

        setEditingRequest(
          null
        )

        setForm({
          ...EMPTY_FORM,
        })

        await loadData()

      } catch (err) {
        showMessage(
          getErrorMessage(
            err,
            editingRequest
              ? "Impossible de modifier la demande."
              : "Impossible de créer la demande."
          ),
          "error"
        )

      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DECISION DIALOG
  // =======================================================

  const openDecisionDialog =
    (
      row,
      action
    ) => {
      const status =
        normalizeStatus(
          row.status
        )

      if (
        (
          action === "APPROVE" ||
          action === "REJECT"
        ) &&
        !isPendingStatus(
          status
        )
      ) {
        showMessage(
          "Cette demande a déjà été traitée.",
          "warning"
        )

        return
      }

      setSelectedRequest(
        row
      )

      setDecisionAction(
        action
      )

      setDecision({
        ...EMPTY_DECISION,
      })

      setDecisionDialogOpen(
        true
      )
    }


  const closeDecisionDialog =
    () => {
      if (
        saving
      ) {
        return
      }

      setDecisionDialogOpen(
        false
      )

      setSelectedRequest(
        null
      )

      setDecisionAction(
        null
      )

      setDecision({
        ...EMPTY_DECISION,
      })
    }


  // =======================================================
  // DECISION
  // =======================================================

  const handleDecision =
    async () => {
      if (
        !selectedRequest ||
        !decisionAction
      ) {
        return
      }

      if (
        decisionAction ===
          "REJECT" &&
        !decision.reason.trim()
      ) {
        showMessage(
          "Indiquez le motif du refus.",
          "warning"
        )

        return
      }

      setSaving(true)

      try {
        let result = null

        if (
          decisionAction ===
          "APPROVE"
        ) {
          result =
            await hrLeaveService
              .approveHrLeaveRequest(
                selectedRequest.id,
                {
                  reason:
                    decision.reason.trim() ||
                    null,
                }
              )
        }

        if (
          decisionAction ===
          "REJECT"
        ) {
          result =
            await hrLeaveService
              .rejectHrLeaveRequest(
                selectedRequest.id,
                {
                  reason:
                    decision.reason.trim() ||
                    null,
                }
              )
        }

        if (
          decisionAction ===
          "CANCEL"
        ) {
          result =
            await hrLeaveService
              .cancelHrLeaveRequest(
                selectedRequest.id,
                {
                  reason:
                    decision.reason.trim() ||
                    null,
                }
              )
        }

        showMessage(
          result?.message ||
          (
            decisionAction ===
              "APPROVE"
              ? "Demande approuvée avec succès."
              : decisionAction ===
                  "REJECT"
                ? "Demande refusée."
                : "Demande annulée."
          )
        )

        closeDecisionDialog()

        await loadData()

      } catch (err) {
        showMessage(
          getErrorMessage(
            err,
            "Impossible d'effectuer cette opération."
          ),
          "error"
        )

      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // TABLE COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field:
            "employee_name",

          headerName:
            "Employé",

          minWidth:
            190,

          flex:
            1.1,

          renderCell:
            (params) => (
              <Box
                sx={{
                  minWidth: 0,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                >
                  {
                    params.value ||
                    params.row.employee_name ||
                    params.row.employee_code ||
                    "-"
                  }
                </Typography>

                {params.row.employee_code && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                  >
                    {
                      params.row.employee_code
                    }
                  </Typography>
                )}
              </Box>
            ),
        },

        {
          field:
            "leave_type",

          headerName:
            "Type",

          minWidth:
            150,

          flex:
            0.85,

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                noWrap
              >
                {
                  params.value ||
                  params.row.leave_type_name ||
                  "-"
                }
              </Typography>
            ),
        },

        {
          field:
            "start_date",

          headerName:
            "Début",

          width:
            112,

          renderCell:
            (params) =>
              formatDateFr(
                params.value
              ),
        },

        {
          field:
            "end_date",

          headerName:
            "Fin",

          width:
            112,

          renderCell:
            (params) =>
              formatDateFr(
                params.value
              ),
        },

        {
          field:
            "requested_days",

          headerName:
            "Jours",

          width:
            78,

          align:
            "center",

          headerAlign:
            "center",

          renderCell:
            (params) =>
              Number(
                params.value ||
                params.row.days ||
                0
              ),
        },

        {
          field:
            "reason",

          headerName:
            "Motif",

          minWidth:
            150,

          flex:
            0.9,

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                noWrap
                title={
                  params.value ||
                  "-"
                }
              >
                {
                  params.value ||
                  "-"
                }
              </Typography>
            ),
        },

        {
          field:
            "status",

          headerName:
            "Statut",

          width:
            118,

          renderCell:
            (params) => {
              const item =
                getStatusConfig(
                  params.value
                )

              return (
                <Chip
                  size="small"
                  label={
                    item.label
                  }
                  color={
                    item.color
                  }
                  sx={{
                    fontWeight: 700,
                    minWidth: 82,
                  }}
                />
              )
            },
        },

        {
          field:
            "actions",

          headerName:
            "Actions",

          minWidth:
            325,

          flex:
            1.35,

          sortable:
            false,

          filterable:
            false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => {
              const status =
                normalizeStatus(
                  params.row.status
                )

              const pending =
                isPendingStatus(
                  status
                )

              const canCancel =
                status !==
                  "CANCELLED" &&
                status !==
                  "REJECTED" &&
                status !==
                  "DECLINED"

              if (
                !pending &&
                !canCancel
              ) {
                return (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Aucune action
                  </Typography>
                )
              }

              return (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  {pending && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        <EditOutlinedIcon
                          fontSize="small"
                        />
                      }
                      onClick={() =>
                        openEditDialog(
                          params.row
                        )
                      }
                      sx={{
                        minWidth: 94,
                        whiteSpace: "nowrap",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                      }}
                    >
                      Modifier
                    </Button>
                  )}

                  {pending && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={
                        <CheckCircleIcon
                          fontSize="small"
                        />
                      }
                      onClick={() =>
                        openDecisionDialog(
                          params.row,
                          "APPROVE"
                        )
                      }
                      sx={{
                        minWidth: 105,
                        whiteSpace: "nowrap",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                      }}
                    >
                      Approuver
                    </Button>
                  )}

                  {pending && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() =>
                        openDecisionDialog(
                          params.row,
                          "REJECT"
                        )
                      }
                      sx={{
                        minWidth: 82,
                        whiteSpace: "nowrap",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                      }}
                    >
                      Refuser
                    </Button>
                  )}

                  {!pending &&
                    canCancel && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      startIcon={
                        <CancelOutlinedIcon
                          fontSize="small"
                        />
                      }
                      onClick={() =>
                        openDecisionDialog(
                          params.row,
                          "CANCEL"
                        )
                      }
                      sx={{
                        minWidth: 90,
                        whiteSpace: "nowrap",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                      }}
                    >
                      Annuler
                    </Button>
                  )}
                </Stack>
              )
            },
        },
      ],
      [
        showMessage,
      ]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.25,
            sm: 2,
            md: 3,
          },

          minWidth: 0,
          width: "100%",
        }}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          mb={2}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2.125rem",
                },

                lineHeight: 1.15,
              }}
            >
              Congés & permissions
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Gestion des demandes, approbations, permissions,
              périodes et décisions RH.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={
                loadData
              }
              disabled={
                loading
              }
              sx={{
                whiteSpace: "nowrap",
                minHeight: 42,
              }}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddIcon />
              }
              onClick={
                openCreateDialog
              }
              sx={{
                whiteSpace: "nowrap",
                minHeight: 42,
              }}
            >
              Nouvelle demande
            </Button>
          </Stack>
        </Stack>


        {/* ================================================= */}
        {/* INFO */}
        {/* ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2.5,
          }}
        >
          La durée est calculée automatiquement entre la date
          de début et la date de fin. Une demande en attente
          peut être modifiée, approuvée ou refusée depuis la
          colonne <strong>Actions</strong>.
        </Alert>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2.5,
            }}
          >
            {error}
          </Alert>
        )}


        {/* ================================================= */}
        {/* KPI */}
        {/* ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },

            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Demandes"
            value={
              summary.total
            }
            subtitle="Total enregistré"
            icon={
              <BeachAccessOutlinedIcon />
            }
          />

          <KpiCard
            title="En attente"
            value={
              summary.pending
            }
            subtitle="Demandes à traiter"
            icon={
              <HourglassEmptyOutlinedIcon />
            }
          />

          <KpiCard
            title="Approuvées"
            value={
              summary.approved
            }
            subtitle="Demandes validées"
            icon={
              <CheckCircleIcon />
            }
          />

          <KpiCard
            title="Refusées"
            value={
              summary.rejected
            }
            subtitle="Demandes non validées"
            icon={
              <CancelOutlinedIcon />
            }
          />
        </Box>


        {/* ================================================= */}
        {/* FILTERS */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 1.5,
              md: 2,
            },

            mb: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg:
                  "minmax(250px, 1.5fr) repeat(3, minmax(160px, 0.7fr))",
              },

              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              placeholder="Employé, matricule ou motif..."
              value={
                search
              }
              onChange={
                (event) =>
                  setSearch(
                    event.target.value
                  )
              }
            />

            <TextField
              select
              size="small"
              label="Employé"
              value={
                employeeFilter
              }
              onChange={
                (event) =>
                  setEmployeeFilter(
                    event.target.value
                  )
              }
            >
              <MenuItem
                value="all"
              >
                Tous les employés
              </MenuItem>

              {employees.map(
                (employee) => (
                  <MenuItem
                    key={
                      employee.id
                    }
                    value={
                      employee.id
                    }
                  >
                    {
                      getEmployeeLabel(
                        employee
                      )
                    }
                  </MenuItem>
                )
              )}
            </TextField>

            <TextField
              select
              size="small"
              label="Type"
              value={
                leaveTypeFilter
              }
              onChange={
                (event) =>
                  setLeaveTypeFilter(
                    event.target.value
                  )
              }
            >
              <MenuItem
                value="all"
              >
                Tous les types
              </MenuItem>

              {leaveTypes.map(
                (leaveType) => (
                  <MenuItem
                    key={
                      leaveType.id
                    }
                    value={
                      leaveType.id
                    }
                  >
                    {
                      getLeaveTypeLabel(
                        leaveType
                      )
                    }
                  </MenuItem>
                )
              )}
            </TextField>

            <TextField
              select
              size="small"
              label="Statut"
              value={
                statusFilter
              }
              onChange={
                (event) =>
                  setStatusFilter(
                    event.target.value
                  )
              }
            >
              <MenuItem
                value="all"
              >
                Tous les statuts
              </MenuItem>

              <MenuItem
                value="PENDING"
              >
                En attente
              </MenuItem>

              <MenuItem
                value="APPROVED"
              >
                Approuvé
              </MenuItem>

              <MenuItem
                value="REJECTED"
              >
                Refusé
              </MenuItem>

              <MenuItem
                value="CANCELLED"
              >
                Annulé
              </MenuItem>
            </TextField>
          </Box>
        </Paper>


        {/* ================================================= */}
        {/* TABLE */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.5,
                md: 2,
              },

              py: 1.75,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
            >
              Demandes de congé et permissions
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.25,
              }}
            >
              Historique des demandes et décisions des ressources humaines.
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",

              height: {
                xs: 480,
                md: 520,
              },

              overflowX: "auto",
            }}
          >
            <DataGrid
              rows={
                rows
              }
              columns={
                columns
              }
              loading={
                loading
              }
              disableRowSelectionOnClick
              rowHeight={60}
              columnHeaderHeight={50}
              pageSizeOptions={[
                5,
                10,
                25,
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              sx={{
                border: 0,
                minWidth: 1220,

                "& .MuiDataGrid-columnHeaders":
                  {
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },

                "& .MuiDataGrid-columnHeaderTitle":
                  {
                    fontWeight: 700,
                  },

                "& .MuiDataGrid-cell":
                  {
                    display: "flex",
                    alignItems: "center",
                  },

                "& .MuiDataGrid-row:hover":
                  {
                    bgcolor: "action.hover",
                  },

                "& .MuiDataGrid-footerContainer":
                  {
                    minHeight: 52,
                  },
              }}
            />
          </Box>
        </Paper>


        {/* ================================================= */}
        {/* CREATE / EDIT DIALOG */}
        {/* ================================================= */}

        <Dialog
          open={
            formDialogOpen
          }
          onClose={() =>
            !saving &&
            setFormDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            fontWeight={900}
          >
            {
              editingRequest
                ? "Modifier la demande"
                : "Nouvelle demande de congé ou permission"
            }
          </DialogTitle>

          <DialogContent>
            <Alert
              severity="info"
              sx={{
                mt: 1,
                mb: 2,
                borderRadius: 2,
              }}
            >
              Durée calculée automatiquement :{" "}
              <strong>
                {requestedDays}{" "}
                jour
                {
                  requestedDays > 1
                    ? "s"
                    : ""
                }
              </strong>
              .
            </Alert>

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },

                gap: 2,
              }}
            >
              <TextField
                select
                required
                disabled={
                  Boolean(
                    editingRequest
                  )
                }
                label="Employé"
                value={
                  form.employee_id
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,
                        employee_id:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem
                  value=""
                >
                  Sélectionner un employé
                </MenuItem>

                {employees.map(
                  (employee) => (
                    <MenuItem
                      key={
                        employee.id
                      }
                      value={
                        employee.id
                      }
                    >
                      {
                        getEmployeeLabel(
                          employee
                        )
                      }
                    </MenuItem>
                  )
                )}
              </TextField>

              <TextField
                select
                required
                label="Type de congé / permission"
                value={
                  form.leave_type_id
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,

                        leave_type_id:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem
                  value=""
                >
                  Sélectionner un type
                </MenuItem>

                {leaveTypes.map(
                  (leaveType) => (
                    <MenuItem
                      key={
                        leaveType.id
                      }
                      value={
                        leaveType.id
                      }
                    >
                      {
                        getLeaveTypeLabel(
                          leaveType
                        )
                      }
                    </MenuItem>
                  )
                )}
              </TextField>

              <TextField
                required
                type="date"
                label="Date de début"
                value={
                  form.start_date
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,

                        start_date:
                          event.target.value,
                      })
                    )
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                required
                type="date"
                label="Date de fin"
                value={
                  form.end_date
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,

                        end_date:
                          event.target.value,
                      })
                    )
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                label="Nombre de jours"
                value={
                  requestedDays
                }
                InputProps={{
                  readOnly: true,
                }}
                helperText="Calcul automatique"
              />

              <TextField
                label="Motif"
                placeholder="Ex. maladie, événement familial..."
                value={
                  form.reason
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,

                        reason:
                          event.target.value,
                      })
                    )
                }
              />

              <TextField
                label="Notes / observations"
                multiline
                minRows={3}
                value={
                  form.notes
                }
                onChange={
                  (event) =>
                    setForm(
                      (current) => ({
                        ...current,

                        notes:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    xs: "auto",
                    md: "1 / -1",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={() =>
                setFormDialogOpen(
                  false
                )
              }
              disabled={
                saving
              }
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={
                handleSave
              }
              disabled={
                saving
              }
            >
              {
                editingRequest
                  ? "Enregistrer les modifications"
                  : "Créer la demande"
              }
            </Button>
          </DialogActions>
        </Dialog>


        {/* ================================================= */}
        {/* DECISION DIALOG */}
        {/* ================================================= */}

        <Dialog
          open={
            decisionDialogOpen
          }
          onClose={
            closeDecisionDialog
          }
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            fontWeight={900}
          >
            {
              decisionAction ===
                "APPROVE"
                ? "Approuver la demande"

                : decisionAction ===
                    "REJECT"
                  ? "Refuser la demande"

                  : "Annuler la demande"
            }
          </DialogTitle>

          <DialogContent>
            {selectedRequest && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 1,
                  mb: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2.5,
                  bgcolor: "action.hover",
                }}
              >
                <Typography
                  fontWeight={800}
                >
                  {
                    selectedRequest.employee_name ||
                    selectedRequest.employee_code ||
                    "Employé"
                  }
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.35,
                  }}
                >
                  {
                    selectedRequest.leave_type ||
                    selectedRequest.leave_type_name ||
                    "Congé / permission"
                  }

                  {" • "}

                  {
                    formatDateFr(
                      selectedRequest.start_date
                    )
                  }

                  {" → "}

                  {
                    formatDateFr(
                      selectedRequest.end_date
                    )
                  }

                  {" • "}

                  {
                    selectedRequest.requested_days ||
                    selectedRequest.days ||
                    0
                  }

                  {" jour(s)"}
                </Typography>
              </Paper>
            )}

            {decisionAction ===
              "APPROVE" && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                Après confirmation, la demande passera de{" "}
                <strong>En attente</strong> à{" "}
                <strong>Approuvé</strong>.
              </Alert>
            )}

            {decisionAction ===
              "REJECT" && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                Le motif du refus est obligatoire pour assurer
                la traçabilité RH.
              </Alert>
            )}

            <TextField
              fullWidth
              required={
                decisionAction ===
                "REJECT"
              }
              label={
                decisionAction ===
                  "REJECT"
                  ? "Motif du refus"

                  : decisionAction ===
                      "CANCEL"
                    ? "Motif de l'annulation"

                    : "Commentaire d'approbation"
              }
              placeholder={
                decisionAction ===
                  "APPROVE"
                  ? "Optionnel"
                  : "Expliquez la décision..."
              }
              multiline
              minRows={3}
              value={
                decision.reason
              }
              onChange={
                (event) =>
                  setDecision({
                    reason:
                      event.target.value,
                  })
              }
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={
                closeDecisionDialog
              }
              disabled={
                saving
              }
            >
              Retour
            </Button>

            <Button
              variant="contained"
              color={
                decisionAction ===
                  "REJECT"
                  ? "error"

                  : decisionAction ===
                      "CANCEL"
                    ? "warning"

                    : "success"
              }
              startIcon={
                decisionAction ===
                  "APPROVE"
                  ? (
                    <CheckCircleIcon />
                  )
                  : (
                    <CancelOutlinedIcon />
                  )
              }
              onClick={
                handleDecision
              }
              disabled={
                saving
              }
            >
              {
                saving
                  ? "Traitement..."

                  : decisionAction ===
                      "APPROVE"
                    ? "Confirmer l'approbation"

                    : decisionAction ===
                        "REJECT"
                      ? "Confirmer le refus"

                      : "Confirmer l'annulation"
              }
            </Button>
          </DialogActions>
        </Dialog>


        {/* ================================================= */}
        {/* SNACKBAR */}
        {/* ================================================= */}

        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            4000
          }
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open: false,
              })
            )
          }
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
            onClose={() =>
              setSnackbar(
                (current) => ({
                  ...current,
                  open: false,
                })
              )
            }
            sx={{
              width: "100%",
            }}
          >
            {
              snackbar.message
            }
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}